import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { ArrowLeft, Save, BookOpen, ExternalLink, CheckCircle2 } from "lucide-react";

/**
 * /admin/with-faith-chapters — Mike edits chapter titles + one-line reads
 * for the Sketch Wall on /with-faith. No code changes needed.
 *
 * Backed by:
 *   GET  /api/with-faith/chapters          (public — returns 20 rows)
 *   POST /api/with-faith/chapters/{n}      (admin — saves title+line for n)
 */

const NAVY = "#0F172A";
const GOLD = "#E8B539";
const PAPER = "#FFFFFF";

export default function AdminWithFaithChaptersPage() {
  const [me, setMe] = useState(undefined);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState({});     // { [n]: true }
  const [saving, setSaving] = useState(null); // current n being saved

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setMe(data);
      } catch { setMe(null); }
    })();
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/with-faith/chapters");
      setRows(data?.chapters || []);
      setDirty({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load chapters");
    }
  };

  useEffect(() => { if (me?.is_admin) load(); }, [me]);

  if (me === undefined) {
    return <div className="min-h-screen flex items-center justify-center">Loading…</div>;
  }
  if (!me) return <Navigate to="/login" replace />;
  if (!me.is_admin) return <Navigate to="/dashboard" replace />;

  const setField = (n, key, val) => {
    setRows((r) => r.map((c) => (c.n === n ? { ...c, [key]: val } : c)));
    setDirty((d) => ({ ...d, [n]: true }));
  };

  const saveRow = async (n) => {
    const row = rows.find((r) => r.n === n);
    if (!row) return;
    setSaving(n);
    try {
      await api.post(`/with-faith/chapters/${n}`, {
        title: row.title || "",
        line:  row.line  || "",
      });
      toast.success(`★ Chapter ${n} saved`);
      setDirty((d) => { const { [n]: _, ...rest } = d; return rest; });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: PAPER }} data-testid="admin-with-faith-chapters">
      <SiteNav />

      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <Link to="/admin/tutorials"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider hover:opacity-80"
                style={{ color: NAVY }}
                data-testid="back-to-admin-btn">
            <ArrowLeft className="w-4 h-4" /> Admin
          </Link>
          <a href="/with-faith" target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border hover:bg-white"
             style={{ borderColor: NAVY, color: NAVY }}
             data-testid="open-book-page-btn">
            View Sketch Wall <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2"
             style={{ color: "#2E9F3A" }}>★ With Faith · Chapter Editor</div>
        <h1 className="font-serif-display text-3xl md:text-4xl uppercase leading-tight mb-3"
            style={{ color: NAVY }}>
          Edit Chapter Titles & One-Liners
        </h1>
        <p className="text-sm md:text-base italic mb-8" style={{ color: "#3D3D3D" }}>
          These show up on the Sketch Wall at <strong>/with-faith</strong>. Edit any chapter,
          hit <strong>Save</strong>, and the page updates instantly. No code, no waitin'.
        </p>

        <div className="space-y-4">
          {rows.map((r) => (
            <div key={r.n}
                 data-testid={`chapter-row-${r.n}`}
                 className="rounded-xl border-2 bg-white p-4 md:p-5 shadow-sm flex flex-col gap-3"
                 style={{ borderColor: dirty[r.n] ? GOLD : "#E5E1D6" }}>
              <div className="flex items-center gap-3">
                <div className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"
                     style={{ background: "#FFFFFF", color: GOLD }}>
                  ★ CHAPTER {r.n}
                </div>
                {!dirty[r.n] && (
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700 font-bold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Saved
                  </span>
                )}
                {dirty[r.n] && (
                  <span className="text-xs font-bold" style={{ color: "#2E9F3A" }}>● Unsaved</span>
                )}
              </div>

              <label className="block">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1.5"
                     style={{ color: NAVY }}>Title</div>
                <input
                  type="text"
                  value={r.title || ""}
                  onChange={(e) => setField(r.n, "title", e.target.value)}
                  placeholder={`Chapter ${r.n} title…`}
                  maxLength={120}
                  data-testid={`chapter-title-input-${r.n}`}
                  className="w-full px-3 py-2.5 rounded-md border-2 text-base focus:outline-none"
                  style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}
                />
              </label>

              <label className="block">
                <div className="text-[10px] tracking-[0.22em] uppercase font-bold mb-1.5"
                     style={{ color: NAVY }}>One-Line Read</div>
                <textarea
                  rows={2}
                  value={r.line || ""}
                  onChange={(e) => setField(r.n, "line", e.target.value)}
                  placeholder="What this chapter is about — in your words, one line."
                  maxLength={240}
                  data-testid={`chapter-line-input-${r.n}`}
                  className="w-full px-3 py-2.5 rounded-md border-2 text-base focus:outline-none resize-none"
                  style={{ borderColor: "#D6D2C9", background: "#FFFFFF" }}
                />
                <div className="text-[10px] mt-1 text-right" style={{ color: "#A89C82" }}>
                  {(r.line || "").length} / 240
                </div>
              </label>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => saveRow(r.n)}
                  disabled={!dirty[r.n] || saving === r.n}
                  data-testid={`chapter-save-btn-${r.n}`}
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-bold uppercase tracking-wider shadow disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02] transition"
                  style={{ background: GOLD, color: NAVY, border: `2px solid ${NAVY}` }}
                >
                  <Save className="w-4 h-4" />
                  {saving === r.n ? "Saving…" : "Save Chapter " + r.n}
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 p-4 rounded-xl border-2 bg-white flex items-start gap-3"
             style={{ borderColor: "#E5E1D6" }}>
          <BookOpen className="w-5 h-5 mt-0.5" style={{ color: NAVY }} />
          <div className="text-sm" style={{ color: "#3D3D3D" }}>
            <strong>Tip:</strong> Keep it real, keep it short. The one-line read shows under
            each sketch — it's a tease, not a summary. If you don't know what to put yet, leave
            it blank and the card will say "One-line read coming from Mike."
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
